// Stripe.js v3 accepts only "modern" publishable keys — anything without a
// `pk_live_` / `pk_test_` prefix is refused at load with:
//
//   IntegrationError: It looks like you're using an older Stripe key. In order
//   to use this API, you'll need to use a modern API key, which is prefixed
//   with 'pk_live_' or 'pk_test_'.
//
// That refusal is invisible to the user: `loadStripe()` returns a promise that
// rejects, `EmbeddedCheckoutProvider` swallows the rejection and never renders,
// and the checkout modal mounts as an empty box. Prod shipped exactly this on
// 2026-08-31 with a legacy pre-2015 `pk_…` key, and because a non-empty string
// read as "billing configured", the CTA stayed live and the backend issued real
// `cs_live_` sessions nobody could pay. Every self-serve purchase — plan
// checkout AND AI-credit top-ups, which share the modal — was silently dead.
//
// So a key Stripe.js will not accept is not a configured key. Normalising it to
// '' makes it fail closed, exactly like an unset key: the CTAs fall back to
// contact-support copy instead of offering a purchase that cannot complete.
const VALID_PREFIXES = ['pk_live_', 'pk_test_'];

export function sanitizeStripePublishableKey(raw: string | undefined | null): string {
  const key = (raw ?? '').trim();
  if (!key) return '';
  if (VALID_PREFIXES.some((prefix) => key.startsWith(prefix))) return key;
  // Deliberately loud. A misconfigured build is otherwise indistinguishable
  // from a dead button, and this console line is the only signal a developer
  // gets before someone reports that they cannot pay. Publishable keys are
  // public by design, but only the prefix is logged — enough to identify the
  // wrong-format value without pasting a credential-shaped string into logs.
  // eslint-disable-next-line no-console
  console.error(
    '[Stripe] Ignoring REACT_APP_STRIPE_PUBLISHABLE_KEY: expected a pk_live_… or '
    + `pk_test_… key, got "${key.slice(0, 8)}…". Self-serve billing is disabled `
    + 'for this build.',
  );
  return '';
}
